import {
  BarChart3,
  Contact,
  DollarSign,
  Headset,
  HelpCircle,
  Home,
  Info,
  LayoutDashboard,
  Mail,
  MessagesSquare,
  Settings,
  Store,
  UserCog,
  UserPlus,
  Users,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Badge } from "src/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { useActiveUser } from "src/hooks/useActiveUser";
import { useAttendantSafe } from "src/providers/AttendantProvider";
import getPermission from "src/utils/getPermission";
import { SidebarLink } from "./styles";

const color = "#77E191";

const ListedItem = ({ icon, route, title, tooltip, badge, isCollapsed }) => {
  const { pathname } = useLocation();

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <SidebarLink to={route}>
            <div
              className={`containerMenuLink ${
                pathname.includes(route) && "selectedMenu"
              } ${isCollapsed ? "justify-center px-2" : ""}`}
            >
              <div className="flex items-center justify-center w-5 h-5 text-current flex-shrink-0">
                {icon}
              </div>
              {!isCollapsed && (
                <>
                  <div className="textMenu flex-1">{title}</div>
                  {badge && (
                    <div className="ml-auto">
                      <Badge
                        variant="secondary"
                        className="text-xs px-2 py-0.5 bg-blue-600 text-white"
                      >
                        {badge}
                      </Badge>
                    </div>
                  )}
                </>
              )}
            </div>
          </SidebarLink>
        </TooltipTrigger>
        {(tooltip || isCollapsed) && (
          <TooltipContent side="right" className="ml-2">
            <p>{isCollapsed ? title : tooltip}</p>
            {isCollapsed && badge && <p className="text-xs text-blue-400">{badge}</p>}
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

const AdminMenuItems = ({ isCollapsed }) => {
  return (
    <div>
      <ListedItem
        route="/admin/dashboard"
        title="Dashboard"
        icon={<LayoutDashboard className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Visão geral do sistema"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/admin/parceiros"
        title="Parceiros"
        icon={<Store className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Gerenciar parceiros do sistema"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/admin/usuarios"
        title="Administradores"
        icon={<Users className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Gerenciar administradores do sistema"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/admin/financeiro"
        title="Financeiro e Transações"
        icon={<DollarSign className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Visualizar informações financeiras"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/admin/afiliados"
        title="Afiliados"
        icon={<UserPlus className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Gerenciar programa de afiliados"
        isCollapsed={isCollapsed}
      />
      {/* Temporarily hidden - Follow-ups and Schedules
      <ListedItem
        route="/admin/follow-ups"
        title="Follow-ups"
        icon={<MessageSquare className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Gerenciar follow-ups automáticos"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/admin/schedules"
        title="Agendamentos"
        icon={<Calendar className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Visualizar agendamentos do sistema"
        isCollapsed={isCollapsed}
      />
      */}
    </div>
  );
};

const SalesMenuItems = ({ isCollapsed }) => {
  const { user } = useActiveUser();

  return (
    <div>
      <ListedItem
        route="/vendas/dashboard"
        title="Dashboard"
        icon={<Home className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Painel de vendas"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/vendas/crm"
        title="CRM"
        icon={<Mail className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Gerenciar leads e oportunidades"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/vendas/clientes"
        title="Clientes"
        icon={<Contact className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Base de clientes de vendas"
        isCollapsed={isCollapsed}
      />
      <ListedItem
        route="/vendas/conta"
        title="Conta"
        icon={<Settings className="imgMenu w-5 h-5" style={{ color: color }} />}
        tooltip="Configurações da conta"
        isCollapsed={isCollapsed}
      />

      {/* Suporte (Sales) */}
      {user.userType === "sales" && (
        <ListedItem
          route="/vendas/suporte"
          title="Suporte"
          icon={<HelpCircle className="imgMenu w-5 h-5" style={{ color: color }} />}
          tooltip="Central de ajuda e suporte"
          isCollapsed={isCollapsed}
        />
      )}
    </div>
  );
};

/**
 * V2 Compact Sidebar Menu Item - Single icon with tooltip
 * Used in the new 50px compact sidebar design
 */
const CompactMenuItem = ({ icon: Icon, path, tooltip, isActive, badge, pendingCount }) => {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={path} aria-label={tooltip} aria-current={isActive ? "page" : undefined} className={`sidebar-nav-item-v2 ${isActive ? "active" : ""}`}>
              <Icon />
              {/* Red pending indicator badge */}
              {pendingCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 text-[10px] text-white rounded-full flex items-center justify-center font-semibold animate-pulse"
                  style={{ boxShadow: "0 0 0 2px rgba(239, 68, 68, 0.3)" }}
                >
                  {pendingCount}
                </span>
              )}
              {/* Regular badge (e.g., "Novo") */}
              {badge && !pendingCount && (
                <span className="absolute -top-2 -right-3 px-1.5 py-0.5 bg-green-500 text-[8px] text-white rounded-full font-bold uppercase leading-none">
                  {badge}
                </span>
              )}
            <span className="sidebar-item-label">{tooltip}</span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={10}>
          <p>{tooltip}</p>
          {pendingCount > 0 && (
            <p className="text-xs text-red-400 font-semibold">
              {pendingCount} pendente{pendingCount > 1 ? "s" : ""}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * V2 Compact Partner Menu - Icon-only navigation for Partner users
 * Based on Figma design: sacGPT-v2
 */
const CompactPartnerMenuItems = ({ currentPath, excludeHome = false }) => {
  const { user } = useActiveUser();
  const hasFullAccess = user?.userGroup?.fullAccess === true;
  const attendantContext = useAttendantSafe();
  const menuItems = [
    { icon: Home, path: "/assistants", tooltip: "Escritório", permission: ["READ_PARTNER_DETAIL"], isHome: true },
    { icon: BarChart3, path: "/estatisticas", tooltip: "Estatísticas", permission: ["READ_PARTNER_USER"] },
    { icon: Headset, path: "/conversas", tooltip: "Atendimento humano", permission: ["READ_ATTENDANT_ACTIVE_CHATS", "READ_ATTENDANT_PENDING_CHATS"], pendingCount: attendantContext?.pendingCount },
    { icon: MessagesSquare, path: "/mensageria", tooltip: "Mensageria", permission: ["READ_PARTNER_DETAIL"] },
    { icon: UserCog, path: "/conta", tooltip: "Conta", permission: ["READ_PAYMENT_SUBSCRIPTION", "READ_MONTLY_USAGE_HISTORY"] },
    { icon: Info, path: "/suporte", tooltip: "Suporte", permission: null },
  ];

  return (
    <div className="partner-sidebar-items">
      {menuItems.filter(item => !excludeHome || !item.isHome).map(item => {
        if (item.permission !== null && !hasFullAccess && !getPermission(item.permission)) return null;
        const isActive = currentPath === item.path || currentPath.startsWith(item.path + "/");
        return <CompactMenuItem key={item.path} {...item} isActive={isActive} />;
      })}
    </div>
  );
};

/**
 * V2 Compact Admin Menu - Icon-only navigation for Admin users
 */
const CompactAdminMenuItems = ({ currentPath, excludeHome = false }) => {
  const menuItems = [
    { icon: LayoutDashboard, path: "/admin/dashboard", tooltip: "Dashboard", isHome: true },
    { icon: Store, path: "/admin/parceiros", tooltip: "Parceiros" },
    { icon: Users, path: "/admin/usuarios", tooltip: "Administradores" },
    { icon: DollarSign, path: "/admin/financeiro", tooltip: "Financeiro" },
    { icon: UserPlus, path: "/admin/afiliados", tooltip: "Afiliados" },
    // Temporarily hidden - Follow-ups and Schedules
    // { icon: MessageSquare, path: '/admin/follow-ups', tooltip: 'Follow-ups' },
    // { icon: Calendar, path: '/admin/schedules', tooltip: 'Agendamentos' },
  ];

  const filteredItems = excludeHome ? menuItems.filter((item) => !item.isHome) : menuItems;

  return (
    <div className="flex flex-col gap-1">
      {filteredItems.map((item, index) => (
        <CompactMenuItem
          key={index}
          icon={item.icon}
          path={item.path}
          tooltip={item.tooltip}
          isActive={currentPath.startsWith(item.path)}
        />
      ))}
    </div>
  );
};

/**
 * V2 Compact Sales Menu - Icon-only navigation for Sales users
 */
const CompactSalesMenuItems = ({ currentPath, excludeHome = false }) => {
  const menuItems = [
    { icon: Home, path: "/vendas/dashboard", tooltip: "Dashboard", isHome: true },
    { icon: Mail, path: "/vendas/crm", tooltip: "CRM" },
    { icon: Contact, path: "/vendas/clientes", tooltip: "Clientes" },
    { icon: Settings, path: "/vendas/conta", tooltip: "Conta" },
    { icon: HelpCircle, path: "/vendas/suporte", tooltip: "Suporte" },
  ];

  const filteredItems = excludeHome ? menuItems.filter((item) => !item.isHome) : menuItems;

  return (
    <div className="flex flex-col gap-1">
      {filteredItems.map((item, index) => (
        <CompactMenuItem
          key={index}
          icon={item.icon}
          path={item.path}
          tooltip={item.tooltip}
          isActive={currentPath.startsWith(item.path)}
        />
      ))}
    </div>
  );
};

/**
 * Main SidebarMenuItems export
 * Supports both legacy (isCollapsed) and v2 (isCompact) modes
 *
 * @param {boolean} excludeHome - When true, excludes the Home/Dashboard item (for unified sidebar with separate home button)
 */
export const SidebarMenuItems = ({ isCollapsed, isCompact, currentPath, excludeHome = false }) => {
  const { user } = useActiveUser();
  const { pathname } = useLocation();
  const activePath = currentPath || pathname;

  // V2 Compact Mode - New design
  if (isCompact) {
    if (user.userType === "admin") {
      return <CompactAdminMenuItems currentPath={activePath} excludeHome={excludeHome} />;
    } else if (user.userType === "partner" || user.userType === "partnerUser") {
      return <CompactPartnerMenuItems currentPath={activePath} excludeHome={excludeHome} />;
    } else if (user.userType === "sales") {
      return <CompactSalesMenuItems currentPath={activePath} excludeHome={excludeHome} />;
    }
    return null;
  }

  // Legacy Mode - Original expanded/collapsed behavior
  if (user.userType === "admin") {
    return <AdminMenuItems isCollapsed={isCollapsed} />;
  } else if (user.userType === "partner" || user.userType === "partnerUser") {
    // F1: o menu expandido do parceiro foi substituído pelo rail compacto (CompactPartnerMenuItems);
    // MainSidebar e AdminSidebar sempre renderizam com isCompact.
    return null;
  } else if (user.userType === "sales") {
    return <SalesMenuItems isCollapsed={isCollapsed} />;
  } else {
    return null;
  }
};
